/**
 * Favorites, at the top of the mail rail: the folders this person pinned,
 * from any mailbox, plus the two combined ones (all inboxes, all sent) they
 * start with. Like Outlook's own Favorites.
 *
 * A pin is added from a row's right-click menu or from the "+" here, which
 * lists everything that can be pinned; it is taken off the same two ways. The
 * list is the person's own and stored on the server, so it follows them to
 * another computer.
 */
import { Check, ChevronDown, Plus } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { ContextMenu, useContextMenu } from "../../../shell/ContextMenu";
import { Pop, useClosePops } from "../../../shell/Pop";
import { ROW_ICON, rowLabel, viewCount, viewLabel } from "./labels";
import { PinItem } from "./PinItem";
import type { MailRail } from "./useMailRail";
import { ALL, BOX_ROWS, refView, rowView, sameView, viewRef, type MailView } from "./view";

export function FavoritesSection({
  rail,
  view,
  onView,
  open,
  onToggle,
}: {
  rail: MailRail;
  view: MailView;
  onView: (view: MailView) => void;
  open: boolean;
  onToggle: () => void;
}) {
  const t = useT();
  const menu = useContextMenu<string>();

  return (
    <section className={cn("crm-acc crm-mbox", open && "is-open")}>
      <div className="crm-mbox-h">
        <button type="button" className="crm-acc-h" aria-expanded={open} onClick={onToggle}>
          <span className="crm-acc-t">{t("mail.fav.title")}</span>
          <ChevronDown className="crm-acc-chev size-3.5" strokeWidth={2.5} />
        </button>
        <AddPin rail={rail} />
      </div>

      {open ? (
        <div className="crm-acc-b">
          {rail.shownFavorites.length === 0 ? (
            <p className="px-[14px] pb-1 text-[11.5px] leading-snug text-muted-foreground">{t("mail.fav.empty")}</p>
          ) : null}
          {rail.shownFavorites.map((ref) => {
            const target = refView(ref)!;
            const { label, sub, Icon } = viewLabel(t, target, rail.mailboxes);
            const n = viewCount(target, rail.mailboxes);
            const on = sameView(view, target);
            return (
              <button
                key={ref}
                type="button"
                className={cn("crm-fold", on && "is-on")}
                aria-current={on ? "true" : undefined}
                onClick={() => onView(target)}
                {...menu.rowProps(ref)}
              >
                <Icon className="size-4" strokeWidth={1.8} />
                <span className="crm-fold-l">
                  {label}
                  {sub ? <span className="crm-fav-sub"> {sub}</span> : null}
                </span>
                {n ? <span className={cn("crm-n", target.kind === "threads" && "crm-n-hot")}>{n}</span> : null}
              </button>
            );
          })}
        </div>
      ) : null}

      <ContextMenu menu={menu} label={t("mail.fav.title")}>
        {(ref, close) => <PinItem pinned onToggle={() => rail.togglePin(ref)} close={close} />}
      </ContextMenu>
    </section>
  );
}

/** The "+" menu: every row that can be pinned, grouped by mailbox, ticked when it is. */
function AddPin({ rail }: { rail: MailRail }) {
  const t = useT();
  const close = useClosePops();

  const item = (target: MailView, label: string, Icon: typeof Plus) => {
    const ref = viewRef(target);
    const pinned = rail.isPinned(ref);
    return (
      <button
        key={ref}
        type="button"
        className={cn("crm-mi", pinned && "is-on")}
        onClick={() => rail.togglePin(ref)}
      >
        <Icon className="size-4" />
        <span className="flex-1">{label}</span>
        {pinned ? <Check className="size-4" /> : null}
      </button>
    );
  };

  return (
    <Pop
      trigger={(isOpen) => (
        <button
          type="button"
          className={cn("crm-cb crm-cb-sm", isOpen && "is-on")}
          title={t("mail.fav.add")}
          aria-label={t("mail.fav.add")}
        >
          <Plus className="size-3.5" />
        </button>
      )}
    >
      <div className="crm-menu-head">
        <div className="crm-menu-t">{t("mail.fav.add")}</div>
        <div className="crm-menu-s">{t("mail.fav.addSub")}</div>
      </div>
      <div className="crm-msect">{t("mail.fav.allMailboxes")}</div>
      {item(rowView(ALL, "inbox"), t("mail.fav.allInbox"), ROW_ICON.inbox)}
      {item(rowView(ALL, "sent"), t("mail.fav.allSent"), ROW_ICON.sent)}
      {rail.mailboxes.map((box) => (
        <div key={box.id}>
          <div className="crm-msect crm-trunc normal-case tracking-normal">{box.address}</div>
          {BOX_ROWS.map((row) => item(rowView(box.id, row), rowLabel(t, row), ROW_ICON[row]))}
        </div>
      ))}
      <div className="crm-menu-sep" />
      <button type="button" className="crm-mi" onClick={close}>
        <Check className="size-4" />
        {t("mail.fav.done")}
      </button>
    </Pop>
  );
}
